import { useEffect, useState } from "react";
import { FileText, Lock, Send, Truck, Wallet, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDate, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { P, StatusPill, openPdf } from "@/components/studio/portalKit";
import { PromptDialog } from "@/components/desk/PromptDialog";

const STATUSES = ["", "Placed", "Proforma Sent", "Payment Received", "Dispatched", "Cancelled"];
const today = () => new Date().toISOString().slice(0, 10);

const dispatchFields = (o) => [
  { key: "courier_name", label: "Courier / transport", required: true },
  { key: "lr_number", label: "LR / docket number" },
  { key: "vehicle_details", label: "Vehicle details" },
  { key: "dispatch_date", label: "Dispatch date", type: "date" },
  ...o.items.filter((i) => i.category === "Sunfilm").map((i, n) => ({ key: `sun_${n}`, label: `Sqft on roll — ${i.name}`, type: "number", required: true })),
];

const OrderCard = ({ o, act, open }) => {
  const st = o.status;
  return (
    <Card data-testid={`desk-order-${o.id}`}>
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="font-semibold text-brand-ink">{o.order_no} · {o.studio_name}</p>
            <p className="text-xs text-slate-500">{fmtDate(o.created_at)}{o.note && ` · Note: ${o.note}`}</p>
          </div>
          <StatusPill status={st} testid={`desk-order-status-${o.id}`} />
        </div>
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-slate-400"><tr><th className="py-1 font-medium">Product</th><th className="text-right font-medium">Qty</th><th className="text-right font-medium">Rate / unit</th></tr></thead>
          <tbody>
            {o.items.map((i) => (
              <tr key={i.sku_id} className="border-b border-slate-100">
                <td className="py-1.5">{i.name}</td><td className="text-right">{i.quantity} {i.unit}</td>
                <td className="text-right">{money(i.hq_sale_price)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p data-testid={`desk-order-total-${o.id}`} className="text-right text-sm font-semibold">Total payable {money(o.total_payable)} <span className="font-normal text-slate-500">(GST {money(o.gst_total)})</span></p>
        {o.payments.length > 0 && <p className="text-xs text-emerald-700">Paid {money(o.payments.at(-1).amount)} · {o.payments.at(-1).mode} · {o.payments.at(-1).reference} · {o.payments.at(-1).date}</p>}
        {o.serials.length > 0 && <p className="text-xs text-slate-600">Serials: {o.serials.map((s) => `${s.serial} (${s.total_capacity} sqft)`).join(", ")}</p>}
        {o.proforma_wa && !o.proforma_wa.sent && <p className="text-xs text-amber-700">WhatsApp not delivered: {o.proforma_wa.reason}</p>}
        <div className="flex flex-wrap gap-2">
          {["Placed", "Proforma Sent"].includes(st) && <Button size="sm" data-testid={`send-proforma-${o.id}`} onClick={() => act(o, "proforma")} className="bg-brand-deep"><Send className="mr-1 h-3.5 w-3.5" />{st === "Placed" ? "Send Proforma" : "Resend Proforma"}</Button>}
          <Button size="sm" variant="outline" data-testid={`view-proforma-${o.id}`} onClick={() => openPdf(`${P}/central/orders/${o.id}/proforma.pdf`)}><FileText className="mr-1 h-3.5 w-3.5" />Proforma</Button>
          {["Placed", "Proforma Sent"].includes(st) && <Button size="sm" variant="outline" data-testid={`mark-paid-${o.id}`} onClick={() => open(o, "payment")}><Wallet className="mr-1 h-3.5 w-3.5" />Mark Payment Received</Button>}
          {st !== "Dispatched" && st !== "Cancelled" && (
            <Button size="sm" data-testid={`dispatch-${o.id}`} disabled={st !== "Payment Received"} onClick={() => open(o, "dispatch")} className="bg-crimson-500 hover:bg-crimson-600">
              {st === "Payment Received" ? <Truck className="mr-1 h-3.5 w-3.5" /> : <Lock className="mr-1 h-3.5 w-3.5" />}Dispatch
            </Button>
          )}
          {o.invoice_available && <Button size="sm" variant="outline" data-testid={`desk-invoice-${o.id}`} onClick={() => openPdf(`${P}/orders/${o.id}/invoice.pdf`)}><FileText className="mr-1 h-3.5 w-3.5" />Invoice</Button>}
          {!["Dispatched", "Cancelled"].includes(st) && <Button size="sm" variant="ghost" data-testid={`cancel-order-${o.id}`} onClick={() => open(o, "cancel")} className="text-red-600"><XCircle className="mr-1 h-3.5 w-3.5" />Cancel</Button>}
        </div>
      </CardContent>
    </Card>
  );
};

export const DeskOrders = ({ onChange }) => {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [dlg, setDlg] = useState(null);
  const load = () => api.get(`${P}/central/orders`, { params: { status: status || undefined } }).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async (fn, msg) => {
    try { const { data } = await fn(); toast.success(msg); if (data?.whatsapp && !data.whatsapp.sent) toast.warning(`WhatsApp: ${data.whatsapp.reason}`); setDlg(null); load(); onChange?.(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const act = (o) => run(() => api.post(`${P}/central/orders/${o.id}/proforma`), "Proforma generated and sent");
  const cfg = dlg && {
    payment: { title: "Mark Payment Received", confirm: "Mark received", initial: { amount: dlg.o.total_payable, date: today() },
      fields: [{ key: "amount", label: "Amount (₹)", type: "number", required: true }, { key: "date", label: "Date", type: "date", required: true },
        { key: "mode", label: "Mode", options: ["UPI", "Bank transfer", "Cash", "Cheque"], required: true }, { key: "reference", label: "Reference" }],
      submit: (v) => run(() => api.post(`${P}/central/orders/${dlg.o.id}/payment`, { ...v, amount: Number(v.amount) }), "Payment recorded") },
    dispatch: { title: "Dispatch order", confirm: "Dispatch", fields: dispatchFields(dlg.o),
      initial: { dispatch_date: today(), ...Object.fromEntries(dlg.o.items.filter((i) => i.category === "Sunfilm").map((i, n) => [`sun_${n}`, i.quantity])) },
      submit: (v) => run(() => api.post(`${P}/central/orders/${dlg.o.id}/dispatch`, { ...v,
        sunfilm_sqft: Object.keys(v).filter((k) => k.startsWith("sun_")).sort().map((k) => Number(v[k])) }), "Dispatched — serials generated") },
    cancel: { title: "Cancel order", confirm: "Cancel order", initial: {}, fields: [{ key: "reason", label: "Reason", required: true }],
      submit: (v) => run(() => api.post(`${P}/central/orders/${dlg.o.id}/cancel`, v), "Order cancelled") },
  }[dlg.kind];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button key={s || "all"} data-testid={`order-filter-${s || "all"}`} onClick={() => setStatus(s)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${status === s ? "bg-brand-deep text-white" : "bg-slate-100 text-slate-600"}`}>{s || "All"}</button>
        ))}
      </div>
      {rows.length === 0 && <p className="text-sm text-slate-500">No studio orders here.</p>}
      {rows.map((o) => <OrderCard key={o.id} o={o} act={act} open={(x, kind) => setDlg({ o: x, kind })} />)}
      {cfg && <PromptDialog open title={cfg.title} fields={cfg.fields} initial={cfg.initial} confirm={cfg.confirm} onClose={() => setDlg(null)} onSubmit={cfg.submit} />}
    </div>
  );
};
